import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { Text } from '../../../../components/Text/Text';
import { dialogOver } from '../../../../components/Sheet/useDialogFocus';
import { useFeatureGate } from '../../../../hooks/useFeatureGate';
import { QaStrip } from '../../../../features/ftux/qa/QaStrip';
import { useLumioControl } from '../../lumioControl';
import { useLumioNudge } from '../../lumioNudge';
import { LUMIO, LUMIO_MOBILE_ANIM } from '../../../../theme/tokens';
import { SparkleGlyph } from '../SparkleGlyph';
import { routeLabel } from '../../routeLabels';
import { BackIcon, ChevronRightIcon, CloseIcon, DecisionIcon, MenuDotsIcon, NewChatIcon, SendIcon, StopIcon, TrayIcon } from '../LumioChat/icons';
import { useLumioChat } from '../LumioChat/useLumioChat';
import { SuggestionCards } from '../LumioChat/SuggestionCards';
import { ConversationMenu } from '../LumioChat/ConversationMenu';
import { SlotMachine } from '../LumioChat/SlotMachine';
import { ChatMessage } from '../LumioChat/types';
import { markdownToHtml, plainToHtml } from '../LumioChat/markdown';
import { renderNudge } from '../NudgeToast/NudgeToast';
import { isDockableNudge, proposalsPreview } from '../LumioChat/briefing';
// Reuse the desktop Lumio welcome + message styled-components verbatim, so the
// copy, suggestion cards, bubbles and markdown body read identically everywhere.
import {
  Greeting,
  Hello,
  Intro,
  IntroStrong,
  Sections,
  MessageRow,
  UserBubble,
  Assistant,
  MarkdownText,
  Typing,
  MetaNote,
  ErrorRow,
  ErrorText,
  RetryButton,
  TrayButton,
  Badge,
  BriefSection,
  Eyebrow,
  NeedsCard,
  NeedsIcon,
  NeedsBody,
  NeedsTitle,
  NeedsSub,
  NeedsChevron,
  NoticedCard,
  NoticedText,
  NoticedDismiss,
  NoticedAction,
  InboxHead,
  BackButton,
  InboxTitle,
  InboxCount,
  ProposalCard,
  ProposalTitle,
  ProposalSummary,
  ChipRow,
  Chip,
  InboxEmpty,
} from '../LumioChat/LumioChat.styles';
import { GlowField } from './GlowField';
import {
  Backdrop,
  CloseButton,
  Composer,
  Footer,
  QaSlot,
  Glyph,
  Header,
  HeaderAction,
  HeaderLeft,
  InputPill,
  Logo,
  LogoMark,
  LogoText,
  Mid,
  Orb,
  Overlay,
  ScrollLock,
  StopOrb,
  Welcome,
} from './LumioMobile.styles';

/**
 * Mobile Lumio — the full-screen "morph" overlay the bottom-nav orb opens
 * (Figma node 8467:18951). Choreography:
 *
 *  1. The nav fades; this overlay's input PILL is anchored on the nav pill's exact
 *     footprint, so it reads as the same element staying put.
 *  2. The send ORB starts where the nav's centre orb was (measured `--orb-travel`)
 *     and slides to the right (the send position); its glyph then swaps spark→send.
 *  3. The dark BACKDROP + lumio glow waves BLOOM in around the pill; the header and
 *     chat content rise/fade in. The chat sits in the middle behind the same
 *     top/bottom fade mask used on desktop.
 *
 * Close reverses it. The component stays mounted (hidden) while on mobile so the
 * conversation survives a close/re-open; `useLumioChat` drives the same Lumio
 * chat backend as the desktop panel.
 */
const LOGO_SRC = '/assets/lumio/lumio-mark.svg';

/** Invisible sentinel marking the live insertion point; swapped for the blinking
 * caret span AFTER markdown rendering (mirrors LumioChat.assistantHtml). */
const CARET_TOKEN = '⁣⁣';
const assistantHtml = (content: string, streaming?: boolean): string =>
  streaming
    ? markdownToHtml(content + CARET_TOKEN).replace(CARET_TOKEN, '<span class="lc-caret"></span>')
    : markdownToHtml(content);

/** Lag the glyph swap so it lands just as the orb finishes sliding (open
 * direction): the orb holds `orbSlideDelayMs`, slides `orbSlideMs`, and the
 * `glyphMs` swap should finish at that end. */
const GLYPH_SWAP_DELAY = Math.max(
  0,
  LUMIO_MOBILE_ANIM.orbSlideDelayMs + LUMIO_MOBILE_ANIM.orbSlideMs - LUMIO_MOBILE_ANIM.glyphMs,
);

export type LumioMobileProps = {
  open: boolean;
  /** Optional so the component can mount bare in CT (function props don't cross the
   * mount boundary); the app always supplies it. */
  onClose?: () => void;
};

export const LumioMobile = ({ open, onClose }: LumioMobileProps): React.ReactElement => {
  const { t } = useTranslation();
  const location = useLocation();
  // RAW label is passed to `useLumioChat` (reserved as assistant context); the
  // welcome copy is now generic, so nothing is interpolated from the route.
  const sectionLabel = routeLabel(location.pathname);

  // The mobile overlay is the active surface only while open, so the welcome
  // fetch (`/chat/init`) fires on first open, not on the hidden mount.
  const {
    messages,
    status,
    isBusy,
    send,
    stop,
    reset,
    retryLast,
    resumeConversation,
    resuming,
    resumeError,
    retryResume,
    welcome,
    retryWelcome,
    pendingProposals,
    decideProposal,
  } = useLumioChat(sectionLabel, open);
  const hasConversation = messages.length > 0;

  // The proposals inbox, same screen as the desktop panel: a plain overlay over
  // whichever middle state is active, closing itself once the last proposal is
  // decided. Without it a mobile owner could never reach their decision chips,
  // although this overlay is the only Lumio surface a phone has.
  const [inboxOpen, setInboxOpen] = useState(false);
  useEffect(() => {
    if (pendingProposals.length === 0) setInboxOpen(false);
  }, [pendingProposals.length]);

  // The live proactive nudge, docked into the briefing as the "Noticed" card.
  // MainLayout suppresses the floating toast while this overlay is open, so
  // without docking it here the nudge would simply vanish for the mobile owner.
  const { nudge, resolve: resolveNudge, reportSurface } = useLumioNudge();
  const nudgeCopy = nudge ? renderNudge(nudge) : null;
  const showNudge = Boolean(nudgeCopy && isDockableNudge(nudge));

  // Report this overlay as a place a nudge can land, on the same terms as the
  // desktop panel, so the layout keeps evaluating while it is open.
  const canDockNudge = open && !hasConversation && !inboxOpen && !resuming && !resumeError;
  useEffect(() => {
    if (!canDockNudge) return undefined;
    reportSurface(1);
    return () => reportSurface(-1);
  }, [canDockNudge, reportSurface]);

  const needsSub = proposalsPreview(pendingProposals, (count) => t('+{{count}} more', { count }));

  // Welcome suggestion cards ship gated (greeting always shows) — revealed live
  // via `show_in_progress`. Default is just the "Hello" welcome. See LumioChat.
  const { isEnabled } = useFeatureGate();
  const showSuggestions = isEnabled('lumio-suggestions');

  // A page asked Lumio to "tell me more": send that seed as the first turn once
  // this overlay is the open (active) surface. Two guards: the shared
  // `consumedNonce` makes the send at-most-once across all surfaces/remounts; the
  // per-instance ref additionally blocks a same-instance re-run (StrictMode
  // double-invoke, `send` re-identity).
  const { seed, consumedNonce, consumeSeed, impersonating = false } = useLumioControl();
  const seededNonceRef = useRef(0);
  useEffect(() => {
    if (!open || !seed || seed.nonce === seededNonceRef.current || seed.nonce <= consumedNonce) return;
    seededNonceRef.current = seed.nonce;
    consumeSeed(seed.nonce);
    setInboxOpen(false); // a seeded turn supersedes the inbox: show the reply
    send(seed.text);
  }, [open, seed, consumedNonce, send, consumeSeed]);

  // Past-conversations flyout, opened from the header "…" button.
  const [historyOpen, setHistoryOpen] = useState(false);
  const dotsRef = useRef<HTMLButtonElement>(null);
  const closeHistory = useCallback(() => setHistoryOpen(false), []);
  const selectConversation = useCallback(
    (conversationId: string) => {
      resumeConversation(conversationId);
      setHistoryOpen(false);
    },
    [resumeConversation],
  );

  // `visible` keeps the overlay in the DOM through the leave animation (display
  // toggles); `entered` drives every enter/leave transition.
  const [visible, setVisible] = useState(open);
  const [entered, setEntered] = useState(false);
  // `armed` paints the orb at its start position with the slide transition DISABLED
  // for one frame, so flipping to `entered` next frame always has a from-state —
  // the open slide can never collapse into a snap (whatever the display timing).
  const [armed, setArmed] = useState(false);
  // `handoff` fades the overlay orb OUT once it has slid home on close, crossfading
  // with the nav orb fading IN behind it (same spot) → a smooth button swap.
  const [handoff, setHandoff] = useState(false);
  // The orb's start/return offset (px) from its resting send position TO the nav
  // orb's actual position — so the slide begins at and returns to exactly the
  // bottom-bar orb, with no overshoot.
  const [orbTravel, setOrbTravel] = useState({ x: 0, y: 0 });
  const [chrome, setChrome] = useState({ header: 0, footer: 0 });
  const [input, setInput] = useState('');

  const overlayRef = useRef<HTMLDivElement>(null);
  const headerRef = useRef<HTMLDivElement>(null);
  const footerRef = useRef<HTMLDivElement>(null);
  const pillRef = useRef<HTMLDivElement>(null);
  const orbRef = useRef<HTMLButtonElement>(null);
  const midRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  // The control that opened us (the nav orb) — focus returns here on close.
  const openerRef = useRef<HTMLElement | null>(null);

  // Mount-machine: show on open; keep visible through the leave transition, then
  // hide (display:none) — the component stays mounted so the chat persists. On
  // close, focus returns to the opener so keyboard/AT users aren't dropped at
  // document start.
  useEffect(() => {
    if (open) {
      openerRef.current = document.activeElement as HTMLElement | null;
      setHandoff(false); // orb solid while open
      setArmed(true); // place the orb at the start with no transition for this frame
      setEntered(false);
      setVisible(true);
      return undefined;
    }
    setArmed(false); // close always animates (slide back), never armed
    setEntered(false);
    openerRef.current?.focus?.();
    // Once the orb has slid home, crossfade it out (the nav orb fades in behind it);
    // then hide the overlay after that crossfade has finished.
    const handoffTimer = window.setTimeout(() => setHandoff(true), LUMIO_MOBILE_ANIM.orbSlideMs);
    const hideTimer = window.setTimeout(() => setVisible(false), LUMIO_MOBILE_ANIM.closeMs);
    return () => {
      window.clearTimeout(handoffTimer);
      window.clearTimeout(hideTimer);
    };
  }, [open]);

  // Measure the chrome heights (for the fade mask) and the orb's resting→nav-orb
  // offset. The orb's resting box is read with its transform cleared; the slide
  // TARGET is the live bottom-bar orb ([data-lumio-nav-orb]) so the morph starts
  // from / returns to its EXACT position (no overshoot). Falls back to the pill
  // centre when the nav orb is absent (CT) or off-screen (hidden-on-scroll).
  const measure = useCallback(() => {
    setChrome((prev) => {
      const header = headerRef.current?.offsetHeight ?? 0;
      const footer = footerRef.current?.offsetHeight ?? 0;
      return prev.header === header && prev.footer === footer ? prev : { header, footer };
    });
    const pill = pillRef.current;
    const orb = orbRef.current;
    if (!pill || !orb) return;
    const prevT = orb.style.transform;
    orb.style.transform = 'none';
    const o = orb.getBoundingClientRect();
    orb.style.transform = prevT;
    const navOrb = document.querySelector('[data-lumio-nav-orb]');
    const navRect = navOrb?.getBoundingClientRect();
    const onScreen = navRect && navRect.top < window.innerHeight && navRect.bottom > 0;
    const target = onScreen ? (navRect as DOMRect) : pill.getBoundingClientRect();
    const x = target.left + target.width / 2 - (o.left + o.width / 2);
    // The morph is a purely HORIZONTAL slide: the nav orb and the overlay orb are
    // centred at the same Y by design (both centred in a 64px pill lifted the same
    // amount off the bottom), so the orb never moves vertically. Forcing y=0 avoids
    // any vertical offset from measuring the nav orb while it's mid-fade/transition.
    const y = 0;
    setOrbTravel((prev) => (prev.x === x && prev.y === y ? prev : { x, y }));
  }, []);

  // On open: measure BEFORE paint (so the armed orb's first painted frame is at the
  // nav-orb position with no transition), force a reflow to commit that frame, then
  // on the next frame disarm + enter so the slide runs from a real start state.
  useLayoutEffect(() => {
    if (!visible || !open) return undefined;
    measure();
    if (overlayRef.current) void overlayRef.current.getBoundingClientRect();
    const id = requestAnimationFrame(() => {
      setArmed(false);
      setEntered(true);
    });
    return () => cancelAnimationFrame(id);
  }, [visible, open, measure]);

  // Track chrome height changes (composer grows) + viewport resizes while open.
  // Both observers funnel through measure() so chrome + orb travel share one source
  // of truth (orb travel depends on width, which a resize can change).
  useEffect(() => {
    if (!visible) return undefined;
    const observer = new ResizeObserver(() => measure());
    if (headerRef.current) observer.observe(headerRef.current);
    if (footerRef.current) observer.observe(footerRef.current);
    window.addEventListener('resize', measure);
    return () => {
      observer.disconnect();
      window.removeEventListener('resize', measure);
    };
  }, [visible, measure]);

  // The Orb (carrying orbRef) is unmounted while streaming; re-measure when it
  // remounts so a re-open begun mid-stream still gets a correct --orb-travel.
  useEffect(() => {
    if (visible && !isBusy) measure();
  }, [visible, isBusy, measure]);

  // Auto-grow the composer (guarded against the hidden mount, like LumioChat).
  const autosize = useCallback(() => {
    const el = textareaRef.current;
    if (!el || el.offsetParent === null) return;
    el.style.height = 'auto';
    el.style.height = `${el.scrollHeight}px`;
  }, []);
  useEffect(() => {
    autosize();
  }, [input, autosize]);

  // Re-fit the composer when it goes hidden→visible (re-open) or on rotation: keyed
  // on WIDTH only, so it never feeds back on our own height writes (mirrors LumioChat).
  useEffect(() => {
    const el = textareaRef.current;
    if (!el) return undefined;
    let lastWidth = el.offsetWidth;
    const observer = new ResizeObserver(() => {
      const width = el.offsetWidth;
      if (width !== lastWidth) {
        lastWidth = width;
        autosize();
      }
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, [autosize]);

  // Focus the composer once the overlay has entered.
  useEffect(() => {
    if (entered) textareaRef.current?.focus();
  }, [entered]);

  // Focus trap: keep Tab/Shift+Tab cycling inside the dialog while it's open, and
  // pull focus back in if it ever lands outside. Paired with the background being
  // aria-hidden (MainLayout/MobileNav) so the modal contract holds for AT.
  useEffect(() => {
    if (!visible || !open) return undefined;
    const onKey = (event: KeyboardEvent): void => {
      if (event.key !== 'Tab') return;
      const root = overlayRef.current;
      // A dialog laid over the overlay (the buy-credits sheet an article request opens) keeps Tab its own.
      if (!root || dialogOver(root)) return;
      const focusables = Array.from(
        root.querySelectorAll<HTMLElement>(
          'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]), [tabindex]:not([tabindex="-1"])',
        ),
      ).filter((el) => el.offsetParent !== null);
      if (focusables.length === 0) return;
      const first = focusables[0];
      const last = focusables[focusables.length - 1];
      const active = document.activeElement as HTMLElement | null;
      if (!root.contains(active)) {
        event.preventDefault();
        first.focus();
      } else if (event.shiftKey && active === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && active === last) {
        event.preventDefault();
        first.focus();
      }
    };
    window.addEventListener('keydown', onKey, true);
    return () => window.removeEventListener('keydown', onKey, true);
  }, [visible, open]);

  // Keep the newest message in view.
  useEffect(() => {
    const el = midRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [messages, status]);

  // Escape closes (or cancels a stream first).
  useEffect(() => {
    if (!visible) return undefined;
    const onKey = (event: KeyboardEvent): void => {
      if (event.key !== 'Escape') return;
      if (isBusy) stop();
      else onClose?.();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [visible, isBusy, stop, onClose]);

  const submit = useCallback(
    (text: string) => {
      if (!text.trim() || isBusy) return;
      // The composer sits outside the middle pane, so it is reachable while the
      // inbox covers the thread. Leave the inbox, or the turn and its reply
      // render behind it and the owner sees their message simply vanish.
      setInboxOpen(false);
      send(text);
      setInput('');
    },
    [isBusy, send],
  );

  const onKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLTextAreaElement>) => {
      if (event.key === 'Enter' && !event.shiftKey) {
        event.preventDefault();
        submit(input);
      } else if (event.key === 'Escape' && isBusy) {
        event.stopPropagation();
        stop();
      }
    },
    [input, isBusy, stop, submit],
  );

  const renderAssistant = (message: ChatMessage): React.ReactElement => {
    const thinking = message.streaming && message.content === '';
    return (
      <Assistant>
        {message.slot ? <SlotMachine result={message.slot} /> : null}
        {thinking ? (
          <Typing aria-label={t('Lumio is thinking')}>
            <span />
            <span />
            <span />
          </Typing>
        ) : message.content ? (
          <MarkdownText dangerouslySetInnerHTML={{ __html: assistantHtml(message.content, message.streaming) }} />
        ) : null}
        {message.stopped && <MetaNote text="Stopped" />}
        {message.error && (
          <ErrorRow>
            <ErrorText text="Lumio couldn’t respond. Please try again." />
            <RetryButton type="button" onClick={retryLast}>
              <Text text="Retry" />
            </RetryButton>
          </ErrorRow>
        )}
      </Assistant>
    );
  };

  const sendDisabled = !input.trim() || resuming;

  return (
    <>
      {visible && <ScrollLock />}
      {/* aria-hidden tracks the visible lifecycle, not the intent flag, so the
          still-focused dialog is never inside an aria-hidden subtree during the
          ~closeMs leave. */}
      <Overlay
        ref={overlayRef}
        $visible={visible}
        role="dialog"
        aria-modal="true"
        aria-label={t('Lumio')}
        aria-hidden={!visible}
        data-ftux-qa-lift=""
      >
        <Backdrop $entered={entered} aria-hidden="true">
          <GlowField />
        </Backdrop>

        <Header ref={headerRef}>
          <HeaderLeft>
            <Logo $entered={entered}>
              <LogoMark>
                {/* Multicolour gradient mark (not recoloured), so a plain img, no fetch-and-inline. */}
                <img src={LOGO_SRC} alt="" />
              </LogoMark>
              <LogoText text="Lumio" />
            </Logo>
            {/* New conversation (compose). */}
            <HeaderAction
              $entered={entered}
              $index={0}
              type="button"
              onClick={reset}
              aria-label={t('New conversation')}
              title={t('New conversation')}
            >
              <NewChatIcon size={22} />
            </HeaderAction>
            {/* Past conversations flyout. */}
            <HeaderAction
              ref={dotsRef}
              $entered={entered}
              $index={1}
              type="button"
              onClick={() => setHistoryOpen((v) => !v)}
              aria-label={t('Past conversations')}
              title={t('Past conversations')}
              aria-haspopup="menu"
              aria-expanded={historyOpen}
            >
              <MenuDotsIcon size={22} />
            </HeaderAction>
            {/* Proposals tray, the same unread-mail-style entry the desktop panel
             * has. Shown only when something is pending, so no dead tray in
             * player mode; the badge and the inbox clear as decisions are made. */}
            {pendingProposals.length > 0 && !inboxOpen && (
              <TrayButton
                type="button"
                onClick={() => setInboxOpen(true)}
                aria-label={t('Proposals')}
                title={t('Proposals')}
              >
                <TrayIcon />
                <Badge aria-hidden="true">{pendingProposals.length}</Badge>
              </TrayButton>
            )}
          </HeaderLeft>
          <CloseButton
            $entered={entered}
            type="button"
            onClick={() => onClose?.()}
            aria-label={t('Close Lumio')}
            title={t('Close Lumio')}
          >
            <CloseIcon />
          </CloseButton>
        </Header>

        <Mid
          ref={midRef}
          $headerH={chrome.header}
          $footerH={chrome.footer}
          $welcome={!hasConversation}
          $entered={entered}
        >
          {inboxOpen ? (
            // Proposals inbox: a dedicated screen over whatever middle state is
            // active, reached from the header tray or the Needs-you card.
            // Deciding the last one empties the list, which closes it.
            <Welcome $entered={entered}>
              <InboxHead>
                <BackButton type="button" onClick={() => setInboxOpen(false)} aria-label={t('Back')}>
                  <BackIcon />
                </BackButton>
                <InboxTitle text="Proposals" />
                {pendingProposals.length > 0 && (
                  <InboxCount text="{{count}} waiting" placeholders={{ count: pendingProposals.length }} />
                )}
              </InboxHead>
              {pendingProposals.length === 0 ? (
                <InboxEmpty text="You’re all caught up. No proposals waiting." />
              ) : (
                pendingProposals.map((proposal) => {
                  // First accept is the primary CTA, other accepts secondary, deny
                  // muted, so multi-action proposals read with a clear hierarchy.
                  const primaryId = proposal.actions.find((a) => a.decision === 'accept')?.id;
                  return (
                    <ProposalCard key={proposal.proposal_id}>
                      <ProposalTitle text={proposal.title} />
                      {proposal.summary && <ProposalSummary text={proposal.summary} />}
                      <ChipRow>
                        {proposal.actions.map((action) => (
                          <Chip
                            key={action.id}
                            type="button"
                            $deny={action.decision === 'deny'}
                            $primary={action.decision === 'accept' && action.id === primaryId}
                            title={action.note || undefined}
                            disabled={isBusy}
                            onClick={() => decideProposal(proposal.proposal_id, action.id)}
                          >
                            {action.label}
                          </Chip>
                        ))}
                      </ChipRow>
                    </ProposalCard>
                  );
                })
              )}
            </Welcome>
          ) : resumeError ? (
            <Welcome $entered={entered}>
              <ErrorRow>
                <ErrorText text="Couldn’t open that conversation." />
                <RetryButton type="button" onClick={retryResume}>
                  <Text text="Retry" />
                </RetryButton>
              </ErrorRow>
            </Welcome>
          ) : resuming ? (
            <Welcome $entered={entered}>
              <Typing aria-label={t('Loading conversation')}>
                <span />
                <span />
                <span />
              </Typing>
            </Welcome>
          ) : hasConversation ? (
            messages.map((message) => (
              <MessageRow key={message.id} $role={message.role}>
                {message.role === 'user' ? (
                  <UserBubble dangerouslySetInnerHTML={{ __html: plainToHtml(message.content) }} />
                ) : (
                  renderAssistant(message)
                )}
              </MessageRow>
            ))
          ) : (
            <Welcome $entered={entered}>
              <Greeting>
                <Hello text="Hello 👋🏼" />
                <Intro>
                  <Text text="I’m" /> <IntroStrong text="Lumio" />
                  <Text text=", your personal AI business partner." />
                </Intro>
              </Greeting>
              {pendingProposals.length > 0 && (
                <BriefSection>
                  <Eyebrow $color={LUMIO.hotTopics}>{t('Needs you')}</Eyebrow>
                  <NeedsCard type="button" onClick={() => setInboxOpen(true)}>
                    <NeedsIcon aria-hidden="true">
                      <DecisionIcon />
                    </NeedsIcon>
                    <NeedsBody>
                      <NeedsTitle
                        text="You have {{count}} decisions waiting on you."
                        placeholders={{ count: pendingProposals.length }}
                      />
                      {needsSub && <NeedsSub text={needsSub} />}
                    </NeedsBody>
                    <NeedsChevron aria-hidden="true">
                      <ChevronRightIcon />
                    </NeedsChevron>
                  </NeedsCard>
                </BriefSection>
              )}
              {showNudge && nudgeCopy && (
                <BriefSection>
                  <Eyebrow $color={LUMIO.link}>{t('Noticed')}</Eyebrow>
                  <NoticedCard>
                    <NoticedDismiss type="button" onClick={() => resolveNudge('dismissed')} aria-label={t('Dismiss')}>
                      ×
                    </NoticedDismiss>
                    <NoticedText text={nudgeCopy.message} />
                    {nudge?.action && nudgeCopy.action && (
                      <NoticedAction
                        type="button"
                        onClick={() => {
                          const message = nudgeCopy.action!.message;
                          resolveNudge('clicked');
                          submit(message);
                        }}
                      >
                        {nudgeCopy.action.label}
                      </NoticedAction>
                    )}
                  </NoticedCard>
                </BriefSection>
              )}
              {showSuggestions && (
                <Sections>
                  <SuggestionCards
                    topics={welcome.topics}
                    loading={welcome.loading}
                    error={welcome.error}
                    onRetry={retryWelcome}
                    onPick={submit}
                  />
                </Sections>
              )}
            </Welcome>
          )}
        </Mid>

        <Footer ref={footerRef}>
          {/* FTUX testers' strip (777ftuxqa): at the foot, over the input (the user, 2026-10-21). A jump or a Restart
              closes Lumio, so the step it starts shows at once. */}
          <QaSlot>
            <QaStrip impersonating={impersonating} onStepChange={onClose} open={open} />
          </QaSlot>
          <InputPill ref={pillRef} $entered={entered}>
            <Composer
              ref={textareaRef}
              $entered={entered}
              rows={1}
              value={input}
              onChange={(event) => setInput(event.target.value)}
              onKeyDown={onKeyDown}
              placeholder={t('Ask me anything...')}
              aria-label={t('Message Lumio')}
            />
            {isBusy ? (
              <StopOrb type="button" onClick={stop} aria-label={t('Stop generating')}>
                <StopIcon />
              </StopOrb>
            ) : (
              <Orb
                ref={orbRef}
                $entered={entered}
                $armed={armed}
                $handoff={handoff}
                $disabled={sendDisabled}
                style={
                  {
                    '--orb-travel-x': `${orbTravel.x}px`,
                    '--orb-travel-y': `${orbTravel.y}px`,
                  } as React.CSSProperties
                }
                type="button"
                onClick={() => submit(input)}
                disabled={sendDisabled}
                aria-label={t('Send message')}
              >
                <Glyph $show={!entered} $delay={entered ? GLYPH_SWAP_DELAY : 0} aria-hidden="true">
                  <SparkleGlyph size={26} />
                </Glyph>
                <Glyph $show={entered} $delay={entered ? GLYPH_SWAP_DELAY : 0} aria-hidden="true">
                  <SendIcon size={24} />
                </Glyph>
              </Orb>
            )}
          </InputPill>
        </Footer>

        <ConversationMenu open={historyOpen} anchorRef={dotsRef} onClose={closeHistory} onSelect={selectConversation} />
      </Overlay>
    </>
  );
};

export default LumioMobile;
